import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowRight, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useSiteContent } from "@/context/SiteContentContext";

/**
 * Hero — minimalist headline block above the Journey selector.
 *
 * The original marketing hero (badge, dual CTAs, animated stats) was
 * replaced with a single headline + subhead + Contact-us button. All
 * copy is admin-editable via `content.hero.*` in the CMS, which the
 * Admin Panel → Content → Hero form already writes.
 */
export function Hero() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { content } = useSiteContent();
  const isAr = i18n.language === "ar";

  const heroCms = content?.hero || {};
  const pick = (en, ar, fallback) => {
    const val = isAr ? ar : en;
    return (val !== undefined && val !== null && String(val).trim() !== "") ? val : fallback;
  };
  const headline = pick(heroCms.headline_en, heroCms.headline_ar, t("hero.headline"));
  const subhead = pick(heroCms.subhead_en, heroCms.subhead_ar, t("hero.subhead"));
  const ctaContact = pick(
    heroCms.cta_contact_en,
    heroCms.cta_contact_ar,
    isAr ? "تواصل معنا" : "Contact us",
  );

  return (
    <section className="relative overflow-hidden" data-testid="hero-simple">
      <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-background to-background pointer-events-none" />
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14 text-center relative">
        <motion.h1
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight"
          data-testid="hero-headline"
        >
          {headline}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.08 }}
          className="text-sm sm:text-base text-muted-foreground mt-3 max-w-2xl mx-auto"
          data-testid="hero-subhead"
        >
          {subhead}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.16 }}
          className="mt-5 flex justify-center"
        >
          <Button
            className="btn-glossy relative overflow-hidden rounded-2xl gap-2 text-base h-12 sm:h-14 px-6 sm:px-7 group"
            onClick={() => navigate("/contact")}
            data-testid="hero-contact-cta"
          >
            <span aria-hidden className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/35 to-transparent animate-[shimmer_2.8s_ease-in-out_infinite]" />
            <span className="relative z-10 flex items-center gap-2">
              <Mail className="h-4 w-4 sm:h-5 sm:w-5" />
              <span>{ctaContact}</span>
              <ArrowRight className={`h-4 w-4 sm:h-5 sm:w-5 transition-transform duration-200 ${isAr ? "rotate-180 group-hover:-translate-x-0.5" : "group-hover:translate-x-0.5"}`} />
            </span>
          </Button>
        </motion.div>
      </div>
    </section>
  );
}
